import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BookOpen, Briefcase, FlaskConical, GraduationCap,
  Hammer, Laptop, Phone, Search, Target, TrendingUp, Users,
} from "lucide-react";
import AdvisorCallbackForm from "@/components/AdvisorCallbackForm";

const HERO_IMG = "https://images.unsplash.com/photo-1648412868424-9bee5023a257?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const WHY_IMG = "https://images.pexels.com/photos/9159042/pexels-photo-9159042.jpeg?auto=compress&cs=tinysrgb&w=1200";
const COMMIT_IMG = "https://images.unsplash.com/photo-1737573477556-ac3ed2db618c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const CTA_IMG = "https://images.unsplash.com/photo-1501504905252-473c47e087f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const decode = (s = "") => s
  .replace(/&nbsp;/g, " ")
  .replace(/&#x27;|&#39;/g, "'")
  .replace(/&quot;/g, '"')
  .replace(/&amp;/g, "&")
  .replace(/&lt;/g, "<")
  .replace(/&gt;/g, ">");

const plain = (html = "") => decode(html.replace(/<[^>]+>/g, " "))
  .replace(/[\u200b\u200c\u200d]/g, "")
  .replace(/\s+/g, " ")
  .trim();

const PILLARS = [
  { icon: GraduationCap, title: "Industry-Relevant Learning", text: "Learning paths stay aligned to current technologies and employer expectations." },
  { icon: Laptop, title: "Learning by Doing", text: "Technology and skill development work together through practical, career-focused training." },
  { icon: Users, title: "Accessible Education", text: "Classroom, corporate and continuing programmes help knowledge reach wider communities." },
  { icon: Briefcase, title: "Career Readiness", text: "Learners build the confidence, capability and professional readiness needed for opportunities." },
];

const JOURNEY = [
  { icon: Search, title: "Discover", text: "Explore the right learning path." },
  { icon: BookOpen, title: "Learn", text: "Gain industry-relevant knowledge." },
  { icon: FlaskConical, title: "Practice", text: "Work on projects and labs." },
  { icon: Hammer, title: "Build", text: "Create a strong portfolio and skills." },
  { icon: TrendingUp, title: "Grow", text: "Move toward your career goals." },
];

function SectionHeading({ eyebrow, title, text, center = false, testid }) {
  return (
    <div className={center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"} data-testid={testid}>
      <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-amber-700">
        <span className="w-7 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" />{eyebrow}
      </span>
      <h2 className="m-0 mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B]">{title}</h2>
      {text && <p className="mt-3 text-[15px] leading-relaxed text-slate-500">{text}</p>}
    </div>
  );
}

export default function MissionPage({ lp }) {
  const raw = lp.body_html || lp.overview_html || "";
  const content = useMemo(() => {
    const h3 = raw.match(/<h3\b[^>]*>([\s\S]*?)<\/h3>/i);
    const paragraphs = [...raw.matchAll(/<p\b[^>]*>([\s\S]*?)<\/p>/gi)]
      .map((m, index) => ({ index, html: m[1], text: plain(m[1]) }))
      .filter((p) => p.text);
    const whyIdx = new Set();
    const commitmentIdx = new Set();
    paragraphs.forEach((p) => {
      if (/single motto|latest technology|bridge the gap/i.test(p.text)) whyIdx.add(p.index);
      if (/take proud|corporate workshops|leadership team|technical training|global technology leaders/i.test(p.text)) commitmentIdx.add(p.index);
    });
    return {
      title: h3 ? { html: h3[1], text: plain(h3[1]) } : null,
      why: paragraphs.filter((p) => whyIdx.has(p.index)),
      commitment: paragraphs.filter((p) => commitmentIdx.has(p.index)),
      legacy: paragraphs.filter((p) => !whyIdx.has(p.index) && !commitmentIdx.has(p.index)),
    };
  }, [raw]);

  const bodyParagraph = ({ html }, key, testid) => (
    <p key={key} className="m-0 text-[15px] leading-relaxed text-slate-600 [&_a]:font-bold [&_a]:text-amber-700 [&_a]:underline [&_strong]:text-[#17263B]"
      dangerouslySetInnerHTML={{ __html: html }} data-testid={testid} />
  );

  return (
    <div className="bg-white text-slate-900" data-testid="mission-page">
      <section className="relative overflow-hidden" data-testid="mission-hero">
        <div className="absolute top-0 right-0 w-72 h-72 bg-[#FFC72C]/30 rounded-full blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-center relative">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.24em] text-slate-500">
              <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" /> Our Mission
            </span>
            <h1 className="m-0 mt-4 text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold tracking-tight leading-[1.05] text-[#17263B]" data-testid="mission-hero-title">
              {lp.h1 || lp.title}
            </h1>
            <p className="m-0 mt-1 text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold tracking-tight leading-[1.05] text-[#FFC72C]" data-testid="mission-hero-motto">
              Qualify for Tomorrow
            </p>
            <p className="m-0 mt-5 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl" data-testid="mission-hero-description">
              {lp.meta_description}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/courses" className="inline-flex items-center gap-2 min-h-[52px] px-6 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] active:scale-[.98] text-[#17263B] font-extrabold shadow-[0_10px_24px_rgba(255,199,44,.32)] transition-all" data-testid="mission-hero-courses">
                Explore Courses <ArrowRight className="w-[18px] h-[18px]" />
              </Link>
              <a href="#mission-advisor" className="inline-flex items-center gap-2 min-h-[52px] px-6 rounded-full border-[1.5px] border-slate-200 bg-white hover:border-[#FFC72C] text-[#17263B] font-extrabold transition-colors" data-testid="mission-hero-advisor">
                Talk to an Advisor
              </a>
            </div>
          </div>
          <div className="relative min-w-0">
            <div className="absolute -left-5 top-8 w-24 h-36 bg-[#FFC72C] rounded-l-3xl" aria-hidden="true" />
            <img src={HERO_IMG} alt="Professional learner studying on a laptop" loading="eager" decoding="async"
              className="relative w-full aspect-[4/3] object-cover rounded-[2rem] shadow-[0_28px_60px_-24px_rgba(23,38,59,0.38)]" data-testid="mission-hero-image" />
            <div className="absolute right-5 top-5 -rotate-3 text-3xl leading-tight text-[#FFC72C] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]" style={{ fontFamily: "'Caveat', cursive" }} data-testid="mission-hero-script">
              Learn.<br />Build.<br />Grow.
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18" data-testid="mission-why">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="relative min-w-0">
            <div className="absolute -left-4 top-8 bottom-8 w-5 rounded-full bg-[#FFC72C]" aria-hidden="true" />
            <img src={WHY_IMG} alt="Learners collaborating in a Training Basket classroom" loading="lazy" decoding="async"
              className="relative w-full aspect-[4/3] object-cover rounded-[2rem] shadow-[0_24px_55px_-24px_rgba(23,38,59,0.32)]" data-testid="mission-why-image" />
            <div className="absolute -right-3 sm:right-8 bottom-8 max-w-[240px] rounded-2xl bg-white shadow-xl border border-slate-100 px-5 py-4 -rotate-2" data-testid="mission-why-note">
              <p className="m-0 text-2xl leading-tight text-slate-800" style={{ fontFamily: "'Caveat', cursive" }}>Technology + Skill</p>
              <span className="block w-16 h-1 bg-[#FFC72C] rounded-full mt-2" aria-hidden="true" />
            </div>
          </div>
          <div className="min-w-0">
            <SectionHeading eyebrow="Why We Exist" title="Education Should Lead to Opportunity" testid="mission-why-heading" />
            {content.title && (
              <h3 className="m-0 mt-6 text-xl font-extrabold text-[#17263B]" dangerouslySetInnerHTML={{ __html: content.title.html }} data-testid="mission-why-cms-h3" />
            )}
            <div className="mt-4 space-y-4">
              {content.why.map((p, i) => bodyParagraph(p, p.index, `mission-why-p-${i}`))}
            </div>
            <div className="mt-6 rounded-2xl bg-[#FFF8D8] border border-[#FFE58A] px-5 py-4 flex items-start gap-3" data-testid="mission-focus-card">
              <span className="w-10 h-10 rounded-full bg-[#FFC72C] flex items-center justify-center shrink-0"><Target className="w-5 h-5 text-[#17263B]" /></span>
              <p className="m-0 text-[15px] leading-relaxed text-[#17263B]"><strong>Our focus is simple:</strong><br />Build Skills. Build Projects. Build Your Career.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] border-y border-slate-100" data-testid="mission-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18">
          <SectionHeading center eyebrow="What Drives Our Mission" title="Our Mission Pillars"
            text="Four practical commitments shape the way Training Basket designs and delivers learning." testid="mission-pillars-heading" />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => (
              <article key={p.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_14px_35px_-24px_rgba(23,38,59,0.28)] hover:-translate-y-1 hover:border-[#FFC72C] transition-all" data-testid={`mission-pillar-${i}`}>
                <span className="w-14 h-14 rounded-full bg-[#FFC72C] flex items-center justify-center"><p.icon className="w-6 h-6 text-[#17263B]" /></span>
                <h3 className="m-0 mt-5 text-lg font-extrabold text-[#17263B]">{p.title}</h3>
                <p className="m-0 mt-2.5 text-sm leading-relaxed text-slate-500">{p.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18" data-testid="mission-commitment">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-center">
          <div className="relative min-w-0">
            <img src={COMMIT_IMG} alt="Professional learner working in a modern training environment" loading="lazy" decoding="async"
              className="w-full aspect-[4/3] object-cover rounded-[2rem] shadow-[0_24px_55px_-24px_rgba(23,38,59,0.32)]" data-testid="mission-commitment-image" />
            <span className="absolute left-5 top-5 -rotate-3 text-3xl leading-tight text-[#FFC72C] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]" style={{ fontFamily: "'Caveat', cursive" }} data-testid="mission-commitment-script">
              Real Skills.<br />Real Careers.
            </span>
          </div>
          <div className="min-w-0">
            <SectionHeading eyebrow="Our Commitment to Every Learner" title="A Supportive Learning Journey, Always" testid="mission-commitment-heading" />
            <div className="mt-5 space-y-4">
              {content.commitment.map((p, i) => bodyParagraph(p, p.index, `mission-commitment-p-${i}`))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] border-y border-slate-100" data-testid="mission-journey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18">
          <SectionHeading center eyebrow="From Learning to Career Growth" title="The Learner Journey"
            text="Career transformation is a journey. Training Basket supports learners from choosing a path to building workplace-ready skills." testid="mission-journey-heading" />
          <ol className="m-0 mt-12 p-0 list-none grid lg:grid-cols-5 gap-7 lg:gap-4 relative">
            <span className="hidden lg:block absolute top-9 left-[10%] right-[10%] border-t-2 border-dashed border-slate-200" aria-hidden="true" />
            {JOURNEY.map((s, i) => (
              <li key={s.title} className="relative text-center" data-testid={`mission-journey-step-${i}`}>
                <span className="relative z-10 w-[74px] h-[74px] rounded-full bg-[#FFC72C] border-8 border-[#F8FAFC] shadow-lg flex items-center justify-center mx-auto">
                  <s.icon className="w-7 h-7 text-[#17263B]" />
                </span>
                <h3 className="m-0 mt-4 text-base font-extrabold text-[#17263B]">{s.title}</h3>
                <p className="m-0 mt-1.5 text-sm leading-relaxed text-slate-500 max-w-[190px] mx-auto">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="mission-advisor" className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18 scroll-mt-24" data-testid="mission-advisor">
        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8 items-start rounded-[2rem] border border-slate-200 bg-white p-5 sm:p-8 shadow-[0_20px_50px_-28px_rgba(23,38,59,0.28)]">
          <div>
            <SectionHeading eyebrow="Talk to an Advisor" title="Get a free counselling call"
              text="Talk to a mentor about batches, fees and career paths." testid="mission-advisor-heading" />
            <a href="tel:+919015887887" className="mt-6 inline-flex items-center gap-3 text-[#17263B] font-extrabold hover:text-amber-700 transition-colors" data-testid="mission-advisor-call">
              <span className="w-11 h-11 rounded-full bg-[#FFC72C] flex items-center justify-center"><Phone className="w-5 h-5" /></span>
              +91 9015 887 887
            </a>
          </div>
          <AdvisorCallbackForm path={lp.url_path} testidPrefix="mission-advisor" />
        </div>
      </section>

      {content.legacy.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-14 sm:pb-18" data-testid="mission-legacy">
          <div className="rounded-[2rem] border border-slate-200 bg-slate-50 p-6 sm:p-8">
            <SectionHeading eyebrow="Legacy Mission Notes" title="Original Mission Notes"
              text="The following historical CMS content is preserved as part of the page record." testid="mission-legacy-heading-block" />
            <div className="mt-5 space-y-4">
              {content.legacy.map((p, i) => bodyParagraph(p, p.index, `mission-legacy-p-${i}`))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20" data-testid="mission-final-cta">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#0F1115] text-white grid lg:grid-cols-[1.15fr_0.85fr] min-h-[320px]">
          <div className="p-7 sm:p-10 lg:p-12 flex flex-col justify-center relative z-10">
            <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.24em] text-[#FFC72C]">
              <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" /> Qualify for Tomorrow
            </span>
            <h2 className="m-0 mt-4 text-3xl sm:text-4xl lg:text-[2.7rem] font-extrabold tracking-tight leading-tight" data-testid="mission-final-title">
              Start your career this month
            </h2>
            <p className="m-0 mt-4 text-[15px] leading-relaxed text-slate-300 max-w-xl" data-testid="mission-final-text">
              Limited seats left for the upcoming batch. Get an instant counsellor callback.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#mission-advisor" className="inline-flex items-center gap-2 min-h-[50px] px-6 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] active:scale-[.98] text-[#17263B] font-extrabold transition-all" data-testid="mission-final-enroll">
                Enrol now for next batch <ArrowRight className="w-[18px] h-[18px]" />
              </a>
              <a href="tel:+919015887887" className="inline-flex items-center gap-2 min-h-[50px] px-6 rounded-full border border-white/25 hover:border-[#FFC72C] hover:text-[#FFC72C] font-extrabold transition-colors" data-testid="mission-final-call">
                <Phone className="w-[18px] h-[18px]" /> Call +91 9015 887 887
              </a>
            </div>
          </div>
          <div className="relative min-h-[220px] lg:min-h-0">
            <img src={CTA_IMG} alt="Notebook and laptop representing career growth" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-70" data-testid="mission-final-image" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F1115] via-[#0F1115]/35 to-transparent" aria-hidden="true" />
            <span className="absolute right-8 top-1/2 -translate-y-1/2 -rotate-3 text-3xl leading-tight text-[#FFC72C]" style={{ fontFamily: "'Caveat', cursive" }} data-testid="mission-final-script">
              Qualify for<br />Tomorrow
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
